import { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, ExternalLink, Star, Calendar, MapPin, X, Save, Loader2, Upload, Image as ImageIcon, Clock, Users, CheckCircle2, Circle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { downscaleImage } from "../../lib/imageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  slug: "",
  title: "",
  tagline: "",
  description: "",
  hero_image_url: "",
  location: "",
  venue: "",
  address: "",
  start_date: "",
  end_date: "",
  start_time: "",
  end_time: "",
  price: "",
  cta_label: "Get in touch",
  cta_url: "",
  highlights: [],
  status: "draft",
  featured: false,
  juried: false,
};

function toHighlights(text) {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function AdminEventsTab({ token }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // event object or EMPTY for new
  const [managingSignups, setManagingSignups] = useState(null); // event object whose signups are being managed
  const [saving, setSaving] = useState(false);
  const [highlightsText, setHighlightsText] = useState("");

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/events`, { headers: authHeaders });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      // Chronological: earliest first, undated events last
      list.sort((a, b) => {
        const ad = a.start_date || "";
        const bd = b.start_date || "";
        if (!ad && bd) return 1;
        if (ad && !bd) return -1;
        return ad.localeCompare(bd);
      });
      setEvents(list);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...EMPTY });
    setHighlightsText("");
  };

  const openEdit = (ev) => {
    setEditing({ ...ev });
    setHighlightsText((ev.highlights || []).join("\n"));
  };

  const close = () => {
    setEditing(null);
    setHighlightsText("");
  };

  const save = async () => {
    if (!editing.slug || !editing.title) {
      toast.error("Slug and title are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, highlights: toHighlights(highlightsText) };
      const isNew = !events.find((e) => e.id === editing.id);
      const url = isNew ? `${API}/admin/events` : `${API}/admin/events/${editing.id}`;
      const method = isNew ? "POST" : "PUT";
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      toast.success(isNew ? "Event created" : "Event updated");
      close();
      load();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/events/${ev.id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Event deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-events-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            Events
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Create, edit, and publish events. Published events show at{" "}
            <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">/events/&#123;slug&#125;</code>
          </p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-[#B85C38] hover:bg-[#8F3E22] text-white px-4 py-2 rounded-full font-semibold transition-colors"
          data-testid="admin-new-event-btn"
        >
          <Plus className="w-4 h-4" /> New event
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading events…</div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300 rounded-xl bg-white">
          <p className="text-gray-500">No events yet. Create your first event above.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`admin-event-row-${ev.slug}`}
            >
              {ev.hero_image_url && (
                <div className="h-32 overflow-hidden bg-gray-100">
                  <img src={ev.hero_image_url} alt={ev.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          ev.status === "published"
                            ? "bg-green-100 text-green-800"
                            : ev.status === "archived"
                              ? "bg-gray-200 text-gray-700"
                              : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {ev.status}
                      </span>
                      {ev.featured && (
                        <span className="text-xs inline-flex items-center gap-1 text-[#C99A3B]">
                          <Star className="w-3 h-3 fill-current" /> Featured
                        </span>
                      )}
                    </div>
                    <h3 className="mt-2 text-lg font-semibold truncate" title={ev.title}>
                      {ev.title}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      /events/<span className="font-mono">{ev.slug}</span>
                    </p>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                      {ev.start_date && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {ev.start_date}
                          {ev.end_date && ev.end_date !== ev.start_date ? ` – ${ev.end_date}` : ""}
                        </span>
                      )}
                      {(ev.start_time || ev.end_time) && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {ev.start_time || ""}{ev.end_time ? ` – ${ev.end_time}` : ""}
                        </span>
                      )}
                      {(ev.venue || ev.location) && (
                        <span className="inline-flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3" />
                          {[ev.venue, ev.location].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => openEdit(ev)}
                    className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-300 rounded-full hover:bg-gray-50"
                    data-testid={`admin-event-edit-${ev.slug}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => setManagingSignups(ev)}
                    className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-300 rounded-full hover:bg-gray-50"
                    data-testid={`admin-event-signups-${ev.slug}`}
                  >
                    <Users className="w-3.5 h-3.5" /> Signups
                  </button>
                  {ev.status === "published" && (
                    <a
                      href={`/events/${ev.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-300 rounded-full hover:bg-gray-50"
                      data-testid={`admin-event-view-${ev.slug}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> View
                    </a>
                  )}
                  <button
                    onClick={() => remove(ev)}
                    className="ml-auto inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-red-200 text-red-700 rounded-full hover:bg-red-50"
                    data-testid={`admin-event-delete-${ev.slug}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EventEditor
          value={editing}
          onChange={setEditing}
          highlightsText={highlightsText}
          setHighlightsText={setHighlightsText}
          onClose={close}
          onSave={save}
          saving={saving}
          token={token}
        />
      )}

      {managingSignups && (
        <SignupsManager
          event={managingSignups}
          token={token}
          onClose={() => setManagingSignups(null)}
        />
      )}
    </div>
  );
}


function SignupsManager({ event, token, onClose }) {
  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ name: "", email: "", phone: "", compass_slug: "", status: "confirmed", notes: "" });
  // Autofill UX state: `looking` shows a spinner on the email field during lookup,
  // `lookupHint` renders a small badge telling the admin what we matched on and
  // which fields we filled, `lookupTriedFor` prevents a second network hit for
  // the same email while they're still typing.
  const [looking, setLooking] = useState(false);
  const [lookupHint, setLookupHint] = useState(null);
  const [lookupTriedFor, setLookupTriedFor] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/events/${event.id}/signups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSignups(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load signups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  const patch = async (signupId, patchBody) => {
    try {
      const res = await fetch(`${API}/admin/events/${event.id}/signups/${signupId}`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify(patchBody),
      });
      if (!res.ok) throw new Error("Update failed");
      const updated = await res.json();
      setSignups((prev) => prev.map((s) => (s.id === signupId ? updated : s)));
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (signupId) => {
    if (!window.confirm("Remove this signup?")) return;
    try {
      const res = await fetch(`${API}/admin/events/${event.id}/signups/${signupId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      setSignups((prev) => prev.filter((s) => s.id !== signupId));
      toast.success("Signup removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const addSignup = async () => {
    if (!newRow.email || !newRow.email.includes("@")) {
      toast.error("A valid email is required");
      return;
    }
    setAdding(true);
    try {
      const res = await fetch(`${API}/admin/events/${event.id}/signups`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(newRow),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Add failed");
      }
      const added = await res.json();
      setSignups((prev) => {
        const idx = prev.findIndex((s) => s.id === added.id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = added;
          return copy;
        }
        return [...prev, added];
      });
      setNewRow({ name: "", email: "", phone: "", compass_slug: "", status: "confirmed", notes: "" });
      setLookupHint(null);
      setLookupTriedFor("");
      toast.success("Signup added");
    } catch (e) {
      toast.error(e.message || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  // Autofill the empty fields of the Add-signup row from the most recent prior
  // signup OR contact matching this email. We never overwrite values the admin
  // has already typed — if they're actively editing a vendor's record on this
  // event, their input wins.
  const lookupVendor = async (rawEmail) => {
    const email = (rawEmail || "").trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    if (email === lookupTriedFor) return; // already looked this one up
    setLookupTriedFor(email);
    setLooking(true);
    setLookupHint(null);
    try {
      const res = await fetch(
        `${API}/admin/vendors/lookup?email=${encodeURIComponent(email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      if (!data?.found) return;

      // Compute which fields are empty right now, then flip them. Doing the
      // compare + setter separately avoids StrictMode's double-invocation
      // dropping dupes into the badge text.
      const candidates = {
        name: data.name,
        phone: data.phone,
        compass_slug: data.compass_slug,
        notes: data.notes,
      };
      const toFill = {};
      const filledFields = [];
      for (const [field, value] of Object.entries(candidates)) {
        if (value && !((newRow[field] || "").trim())) {
          toFill[field] = value;
          filledFields.push(field);
        }
      }
      if (filledFields.length) {
        setNewRow((prev) => ({ ...prev, ...toFill }));
      }

      // Describe what we matched + where it came from. We always want the
      // admin to be able to tell at a glance that fields were pre-filled.
      const srcParts = [];
      if (data.source?.signup_event_slug) {
        srcParts.push(`previous signup on ${data.source.signup_event_slug}`);
      } else if (data.source?.contact_date) {
        srcParts.push("prior contact form");
      }
      setLookupHint({
        found: true,
        filled: filledFields,
        source: srcParts.join(" + ") || "prior records",
      });
    } catch {
      // network failures are silent — the admin can just fill the form manually.
    } finally {
      setLooking(false);
    }
  };

  const confirmedCount = signups.filter((s) => s.status === "confirmed").length;
  const pendingCount = signups.filter((s) => s.status === "pending").length;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-signups-modal"
      >
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-[#B85C38]" /> Signups &mdash; {event.title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {event.juried ? "Juried event" : "Open signup"} &middot; {confirmedCount} confirmed
              {pendingCount ? ` \u00b7 ${pendingCount} pending` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
            data-testid="admin-signups-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* Add signup */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <p className="text-sm font-semibold mb-3">Add a vendor manually</p>
            <div className="grid md:grid-cols-6 gap-2">
              <input
                className="admin-input md:col-span-2"
                placeholder="Vendor name"
                value={newRow.name}
                onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
                data-testid="admin-signup-add-name"
              />
              <div className="md:col-span-2 relative">
                <input
                  className="admin-input w-full"
                  placeholder="Email *"
                  type="email"
                  value={newRow.email}
                  onChange={(e) => {
                    setNewRow({ ...newRow, email: e.target.value });
                    // If they edit the email after a lookup, clear the hint so the
                    // badge doesn't lie about which record was matched.
                    if (lookupHint) setLookupHint(null);
                    if (lookupTriedFor && e.target.value.trim().toLowerCase() !== lookupTriedFor) {
                      setLookupTriedFor("");
                    }
                  }}
                  onBlur={(e) => lookupVendor(e.target.value)}
                  data-testid="admin-signup-add-email"
                />
                {looking && (
                  <Loader2
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin"
                    data-testid="admin-signup-lookup-spinner"
                  />
                )}
              </div>
              <input
                className="admin-input"
                placeholder="Compass slug or full URL"
                value={newRow.compass_slug}
                onChange={(e) => setNewRow({ ...newRow, compass_slug: e.target.value })}
                title="Paste a Compass slug (e.g. s-gardner) OR any full URL (e.g. https://etsy.com/shop/foo) — the vendor's name on the event page links straight to it."
                data-testid="admin-signup-add-compass"
              />
              <select
                className="admin-input"
                value={newRow.status}
                onChange={(e) => setNewRow({ ...newRow, status: e.target.value })}
                data-testid="admin-signup-add-status"
              >
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              {lookupHint?.found && lookupHint.filled.length > 0 ? (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-xs"
                  data-testid="admin-signup-lookup-hint"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Autofilled {lookupHint.filled.join(", ")} from {lookupHint.source}
                </div>
              ) : lookupHint?.found === true ? (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs"
                  data-testid="admin-signup-lookup-hint"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Vendor found — nothing to fill (you've already typed everything)
                </div>
              ) : (
                <span />
              )}
              <button
                onClick={addSignup}
                disabled={adding}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#B85C38] text-white rounded-full text-sm disabled:opacity-50"
                data-testid="admin-signup-add-btn"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add signup
              </button>
            </div>
          </div>

          {/* Signup table */}
          {loading ? (
            <p className="text-gray-500">Loading signups…</p>
          ) : signups.length === 0 ? (
            <p className="text-center text-gray-500 py-8" data-testid="admin-signups-empty">
              No signups yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-signups-table">
                <thead>
                  <tr className="text-left border-b bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Name / Email</th>
                    <th className="px-3 py-2">Compass slug</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {signups.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b hover:bg-gray-50"
                      data-testid={`admin-signup-row-${s.id}`}
                    >
                      <td className="px-3 py-2">
                        <button
                          onClick={() =>
                            patch(s.id, { status: s.status === "confirmed" ? "pending" : "confirmed" })
                          }
                          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
                            s.status === "confirmed"
                              ? "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                              : "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                          }`}
                          title="Click to toggle"
                          data-testid={`admin-signup-toggle-${s.id}`}
                        >
                          {s.status === "confirmed" ? (
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          ) : (
                            <Circle className="w-3.5 h-3.5" />
                          )}
                          {s.status}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{s.name || "—"}</div>
                        <div className="text-xs text-gray-500">{s.email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          className="admin-input py-1 text-xs"
                          value={s.compass_slug || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSignups((prev) =>
                              prev.map((row) => (row.id === s.id ? { ...row, compass_slug: v } : row))
                            );
                          }}
                          onBlur={(e) => patch(s.id, { compass_slug: e.target.value.trim() })}
                          placeholder="slug or https://…"
                          title="Compass slug (e.g. s-gardner) OR any full URL (etsy, personal site, etc.)"
                          data-testid={`admin-signup-compass-${s.id}`}
                        />
                        {s.compass_url && (
                          <a
                            href={s.compass_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-xs text-[#B85C38] hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" /> Preview
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 capitalize">{s.source}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => remove(s.id)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-red-200 text-red-700 rounded-full hover:bg-red-50"
                          data-testid={`admin-signup-delete-${s.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EventEditor({ value, onChange, highlightsText, setHighlightsText, onClose, onSave, saving, token }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const isNew = !value.id;
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleHeroFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    setUploading(true);
    try {
      const prepared = await downscaleImage(file);
      const fd = new FormData();
      fd.append("file", prepared, prepared.name || file.name);
      const res = await fetch(`${API}/admin/events/upload-hero`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const data = await res.json();
      set("hero_image_url", data.url);
      toast.success("Hero image uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="admin-event-editor">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {isNew ? "New event" : "Edit event"}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="admin-event-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Slug (url-safe)">
              <input
                type="text"
                value={value.slug}
                onChange={(e) => set("slug", e.target.value)}
                className="admin-input"
                placeholder="festival-of-trees-2026"
                data-testid="admin-event-slug"
              />
            </Field>
            <Field label="Title">
              <input
                type="text"
                value={value.title}
                onChange={(e) => set("title", e.target.value)}
                className="admin-input"
                data-testid="admin-event-title"
              />
            </Field>
          </div>

          <Field label="Tagline">
            <input
              type="text"
              value={value.tagline || ""}
              onChange={(e) => set("tagline", e.target.value)}
              className="admin-input"
              placeholder="A short editorial phrase"
              data-testid="admin-event-tagline"
            />
          </Field>

          <Field label="Hero image">
            <div className="space-y-2">
              {value.hero_image_url && (
                <div className="relative w-full max-w-md h-40 overflow-hidden rounded-lg bg-gray-100 border border-gray-200">
                  <img
                    src={value.hero_image_url}
                    alt="Hero preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleHeroFile(f);
                    e.target.value = "";
                  }}
                  data-testid="admin-event-hero-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-full text-sm hover:bg-gray-50 disabled:opacity-50"
                  data-testid="admin-event-hero-upload-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Uploading…" : value.hero_image_url ? "Replace image" : "Upload from computer"}
                </button>
                {value.hero_image_url && (
                  <button
                    type="button"
                    onClick={() => set("hero_image_url", "")}
                    className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-red-200 text-red-700 rounded-full hover:bg-red-50"
                    data-testid="admin-event-hero-clear"
                  >
                    <X className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>
              <input
                type="text"
                value={value.hero_image_url || ""}
                onChange={(e) => set("hero_image_url", e.target.value)}
                className="admin-input"
                placeholder="…or paste an image URL"
                data-testid="admin-event-hero"
              />
              <p className="text-xs text-gray-500">
                <ImageIcon className="w-3 h-3 inline mr-1" />
                Large photos are automatically downscaled before upload.
              </p>
            </div>
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Venue">
              <input
                type="text"
                value={value.venue || ""}
                onChange={(e) => set("venue", e.target.value)}
                className="admin-input"
                data-testid="admin-event-venue"
              />
            </Field>
            <Field label="Location (city, region)">
              <input
                type="text"
                value={value.location || ""}
                onChange={(e) => set("location", e.target.value)}
                className="admin-input"
                data-testid="admin-event-location"
              />
            </Field>
          </div>

          <Field label="Physical address (used for map + directions)">
            <input
              type="text"
              value={value.address || ""}
              onChange={(e) => set("address", e.target.value)}
              className="admin-input"
              placeholder="123 Main St, Leesburg, VA 20176, USA"
              data-testid="admin-event-address"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Start date">
              <input
                type="date"
                value={value.start_date || ""}
                onChange={(e) => set("start_date", e.target.value)}
                className="admin-input"
                data-testid="admin-event-start"
              />
            </Field>
            <Field label="End date">
              <input
                type="date"
                value={value.end_date || ""}
                onChange={(e) => set("end_date", e.target.value)}
                className="admin-input"
                data-testid="admin-event-end"
              />
            </Field>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Start time">
              <input
                type="time"
                value={value.start_time || ""}
                onChange={(e) => set("start_time", e.target.value)}
                className="admin-input"
                data-testid="admin-event-start-time"
              />
            </Field>
            <Field label="End time">
              <input
                type="time"
                value={value.end_time || ""}
                onChange={(e) => set("end_time", e.target.value)}
                className="admin-input"
                data-testid="admin-event-end-time"
              />
            </Field>
            <Field label="Price">
              <input
                type="text"
                value={value.price || ""}
                onChange={(e) => set("price", e.target.value)}
                className="admin-input"
                placeholder="Free / $25 / Sliding scale"
                data-testid="admin-event-price"
              />
            </Field>
          </div>

          <Field label="Description (HTML supported)">
            <textarea
              value={value.description || ""}
              onChange={(e) => set("description", e.target.value)}
              rows={6}
              className="admin-input font-mono text-sm"
              placeholder="<p>Write your editorial description here…</p>"
              data-testid="admin-event-description"
            />
          </Field>

          <Field label="Highlights (one per line)">
            <textarea
              value={highlightsText}
              onChange={(e) => setHighlightsText(e.target.value)}
              rows={4}
              className="admin-input"
              placeholder="80+ juried artisans&#10;Live music&#10;Kids' craft corner"
              data-testid="admin-event-highlights"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="CTA label">
              <input
                type="text"
                value={value.cta_label || ""}
                onChange={(e) => set("cta_label", e.target.value)}
                className="admin-input"
                data-testid="admin-event-cta-label"
              />
            </Field>
            <Field label="CTA URL (optional)">
              <input
                type="text"
                value={value.cta_url || ""}
                onChange={(e) => set("cta_url", e.target.value)}
                className="admin-input"
                data-testid="admin-event-cta-url"
              />
            </Field>
          </div>

          <div className="flex items-center gap-6 pt-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <span className="text-gray-500 uppercase text-xs tracking-wide">Status</span>
              <select
                value={value.status}
                onChange={(e) => set("status", e.target.value)}
                className="admin-input"
                data-testid="admin-event-status"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!value.featured}
                onChange={(e) => set("featured", e.target.checked)}
                className="accent-[#B85C38]"
                data-testid="admin-event-featured"
              />
              <Star className="w-4 h-4 text-[#C99A3B]" />
              Featured
            </label>
            <label className="inline-flex items-center gap-2 text-sm" title="Juried events send a review-and-approve email; non-juried events send an immediate confirmation email.">
              <input
                type="checkbox"
                checked={!!value.juried}
                onChange={(e) => set("juried", e.target.checked)}
                className="accent-[#B85C38]"
                data-testid="admin-event-juried"
              />
              <span className="inline-flex items-center gap-1">
                Juried
                <span className="text-xs text-gray-500">(applications reviewed before acceptance)</span>
              </span>
            </label>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-gray-300 text-sm hover:bg-gray-50"
            data-testid="admin-event-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#B85C38] hover:bg-[#8F3E22] text-white px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50"
            data-testid="admin-event-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : isNew ? "Create event" : "Save changes"}
          </button>
        </div>

        <style>{`
          .admin-input {
            width: 100%;
            padding: 0.55rem 0.8rem;
            border: 1px solid #d1d5db;
            border-radius: 0.5rem;
            font-family: 'Manrope', sans-serif;
            font-size: 0.9rem;
            transition: border-color 0.15s ease;
          }
          .admin-input:focus { outline: none; border-color: #B85C38; }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
